import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  Square,
  Play,
  Pause,
  Video,
  Upload,
  Clock,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Brain,
  FileText,
  ListTodo,
  Users,
  RefreshCw,
  Trash2,
  Eye,
  Radio,
  Waves,
  Volume2,
  User,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EnregistrementAvancePage() {
  const { token } = useAuth();
  
  // États enregistrement
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [titre, setTitre] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [chunksSent, setChunksSent] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // États sessions
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Refs pour l'enregistrement
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const chunkIntervalRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Charger les sessions
  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await fetch(`${API_URL}/api/recording/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Erreur chargement sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  }, [token]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Timer d'enregistrement
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, isPaused]);

  // Formater le temps
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Visualisation audio
  const updateAudioLevel = useCallback(() => {
    if (analyserRef.current && isRecording && !isPaused) {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  }, [isRecording, isPaused]);

  // Démarrer l'enregistrement
  const startRecording = async () => {
    if (!titre.trim()) {
      toast.error('Titre requis', { description: 'Donnez un nom à votre enregistrement' });
      return;
    }

    try {
      // 1. Créer la session sur le serveur
      const startRes = await fetch(`${API_URL}/api/recording/session/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          titre: titre,
          type_reunion: 'visio'
        })
      });
      
      const startData = await startRes.json();
      if (!startData.success) {
        throw new Error(startData.detail || 'Erreur création session');
      }
      
      setSessionId(startData.session_id);

      // 2. Demander l'accès au micro
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      streamRef.current = stream;

      // 3. Créer le contexte audio pour la visualisation
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // 4. Créer le MediaRecorder
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      chunksRef.current = [];
      let chunkIndex = 0;

      mediaRecorderRef.current.ondataavailable = async (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      // 5. Envoyer les chunks toutes les 30 secondes
      chunkIntervalRef.current = setInterval(async () => {
        if (chunksRef.current.length > 0 && sessionId) {
          const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
          chunksRef.current = [];
          
          await sendChunk(startData.session_id, audioBlob, chunkIndex);
          chunkIndex++;
          setChunksSent(chunkIndex);
        }
      }, 30000); // Toutes les 30 secondes

      // 6. Démarrer l'enregistrement (chunks de 10s pour buffer)
      mediaRecorderRef.current.start(10000);

      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setChunksSent(0);

      // Démarrer la visualisation
      updateAudioLevel();

      toast.success('Enregistrement démarré', {
        description: 'Vous pouvez changer d\'onglet, l\'enregistrement continue en arrière-plan'
      });

    } catch (error) {
      console.error('Erreur démarrage:', error);
      toast.error('Erreur', { description: error.message });
    }
  };

  // Envoyer un chunk au serveur
  const sendChunk = async (sessId, audioBlob, index) => {
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, `chunk_${index}.webm`);
      formData.append('chunk_index', index.toString());

      await fetch(`${API_URL}/api/recording/session/${sessId}/chunk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
    } catch (error) {
      console.error('Erreur envoi chunk:', error);
    }
  };

  // Mettre en pause
  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        updateAudioLevel();
        toast.info('Enregistrement repris');
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        toast.info('Enregistrement en pause');
      }
    }
  };

  // Arrêter l'enregistrement
  const stopRecording = async () => {
    if (!mediaRecorderRef.current || !sessionId) return;

    try {
      // Arrêter l'intervalle d'envoi
      if (chunkIntervalRef.current) {
        clearInterval(chunkIntervalRef.current);
      }

      // Arrêter la visualisation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Attendre que le MediaRecorder s'arrête
      await new Promise((resolve) => {
        mediaRecorderRef.current.onstop = async () => {
          // Envoyer les derniers chunks
          if (chunksRef.current.length > 0) {
            const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
            await sendChunk(sessionId, audioBlob, chunksSent);
          }
          resolve();
        };
        mediaRecorderRef.current.stop();
      });

      // Arrêter le stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // Fermer le contexte audio
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      // Signaler la fin de la session au serveur
      const stopRes = await fetch(`${API_URL}/api/recording/session/${sessionId}/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const stopData = await stopRes.json();

      toast.success('Enregistrement terminé !', {
        description: `${chunksSent + 1} segments envoyés. Analyse en cours...`
      });

      // Reset
      setIsRecording(false);
      setIsPaused(false);
      setAudioLevel(0);
      setSessionId(null);
      setTitre('');
      
      // Recharger les sessions
      loadSessions();

    } catch (error) {
      console.error('Erreur arrêt:', error);
      toast.error('Erreur', { description: error.message });
    }
  };

  // Voir le détail d'une session
  const viewSession = async (sessId) => {
    try {
      const res = await fetch(`${API_URL}/api/recording/sessions/${sessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSelectedSession(data.session);
      }
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible de charger la session' });
    }
  };

  // Supprimer une session
  const deleteSession = async (sessId) => {
    if (!confirm('Supprimer cet enregistrement ?')) return;
    
    try {
      await fetch(`${API_URL}/api/recording/sessions/${sessId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session supprimée');
      loadSessions();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  // Rendu du statut
  const renderStatus = (status) => {
    const statusMap = {
      'recording': { label: 'En cours', variant: 'default', icon: Radio },
      'processing': { label: 'Traitement...', variant: 'secondary', icon: Loader2 },
      'completed': { label: 'Terminé', variant: 'default', icon: CheckCircle2 },
      'failed': { label: 'Échec', variant: 'destructive', icon: AlertCircle },
    };
    const s = statusMap[status] || { label: status, variant: 'outline', icon: Clock };
    const Icon = s.icon;
    return (
      <Badge variant={s.variant}>
        <Icon className={`w-3 h-3 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {s.label}
      </Badge>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mic className="w-7 h-7 text-primary" />
          Enregistrement Avancé
        </h1>
        <p className="text-muted-foreground">
          Enregistrez en arrière-plan avec identification des speakers
        </p>
      </div>

      {/* Zone d'enregistrement */}
      <Card className={`border-2 transition-colors ${isRecording ? 'border-red-500 bg-red-50/50 dark:bg-red-950/20' : 'border-dashed border-primary/30'}`}>
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center space-y-6">
            
            {/* Titre */}
            {!isRecording && (
              <div className="w-full max-w-md">
                <Label htmlFor="titre">Nom de la réunion</Label>
                <Input
                  id="titre"
                  placeholder="Ex: Réunion équipe, Appel client Dupont..."
                  value={titre}
                  onChange={(e) => setTitre(e.target.value)}
                  className="mt-1"
                  data-testid="recording-title"
                />
              </div>
            )}

            {/* Bouton principal */}
            <div className="relative">
              {/* Indicateur de niveau audio */}
              {isRecording && !isPaused && (
                <div 
                  className="absolute inset-0 rounded-full bg-red-500/20"
                  style={{ 
                    transform: `scale(${1 + audioLevel * 0.6})`,
                    transition: 'transform 0.1s ease-out'
                  }}
                />
              )}
              
              <Button
                size="lg"
                variant={isRecording ? "destructive" : "default"}
                className={`w-28 h-28 rounded-full text-lg font-semibold shadow-lg transition-all ${
                  isRecording && !isPaused ? 'animate-pulse' : ''
                }`}
                onClick={isRecording ? stopRecording : startRecording}
                data-testid="btn-record-main"
              >
                <div className="flex flex-col items-center gap-1">
                  {isRecording ? (
                    <>
                      <Square className="w-8 h-8" />
                      <span className="text-xs">Arrêter</span>
                    </>
                  ) : (
                    <>
                      <Mic className="w-8 h-8" />
                      <span className="text-xs">Démarrer</span>
                    </>
                  )}
                </div>
              </Button>
            </div>

            {/* Bouton pause (si enregistrement en cours) */}
            {isRecording && (
              <Button
                variant="outline"
                size="sm"
                onClick={pauseRecording}
                className="gap-2"
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                {isPaused ? 'Reprendre' : 'Pause'}
              </Button>
            )}

            {/* Infos enregistrement */}
            {isRecording && (
              <div className="space-y-3 w-full max-w-md">
                <div className="flex items-center justify-center gap-6 text-lg">
                  <div className="flex items-center gap-2 text-red-500">
                    <span className={`w-3 h-3 bg-red-500 rounded-full ${!isPaused ? 'animate-pulse' : ''}`} />
                    <span className="font-mono font-bold">{formatTime(recordingTime)}</span>
                  </div>
                  {isPaused && (
                    <Badge variant="secondary">En pause</Badge>
                  )}
                </div>

                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Radio className="w-4 h-4 text-green-500" />
                    {chunksSent} segments envoyés
                  </span>
                </div>

                {/* Barre de niveau audio */}
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-muted-foreground" />
                  <Progress value={audioLevel * 100} className="flex-1 h-2" />
                </div>

                <p className="text-xs text-muted-foreground">
                  Vous pouvez changer d'onglet ou d'application.
                  L'enregistrement continue en arrière-plan.
                </p>
              </div>
            )}

            {!isRecording && (
              <div className="text-sm text-muted-foreground space-y-2">
                <p className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Fonctionne en arrière-plan
                </p>
                <p className="flex items-center justify-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  Identification des speakers automatique
                </p>
                <p className="flex items-center justify-center gap-2">
                  <Brain className="w-4 h-4 text-purple-500" />
                  Résumé et actions extraits par l'IA
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Liste des sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Mes enregistrements
              </CardTitle>
              <CardDescription>
                {sessions.length} enregistrement{sessions.length > 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadSessions}
              disabled={loadingSessions}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingSessions ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingSessions ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mic className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun enregistrement</p>
              <p className="text-sm">Démarrez votre premier enregistrement ci-dessus</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-primary/10 rounded-full">
                      <Mic className="w-5 h-5 text-primary" />
                    </div>
                    
                    <div>
                      <h3 className="font-medium">{session.titre}</h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(session.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {session.transcription?.speakers_count > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {session.transcription.speakers_count} speaker{session.transcription.speakers_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {renderStatus(session.status)}
                    
                    {session.status === 'completed' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewSession(session.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteSession(session.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal détail session */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedSession.titre}</CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    {selectedSession.transcription?.speakers_count > 0 && (
                      <Badge variant="outline">
                        <Users className="w-3 h-3 mr-1" />
                        {selectedSession.transcription.speakers_count} speaker{selectedSession.transcription.speakers_count > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {renderStatus(selectedSession.status)}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedSession(null)}>
                  Fermer
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs defaultValue="analyse">
                <TabsList className="w-full rounded-none border-b">
                  <TabsTrigger value="analyse">Analyse IA</TabsTrigger>
                  <TabsTrigger value="transcription">Transcription</TabsTrigger>
                  <TabsTrigger value="speakers">Par Speaker</TabsTrigger>
                </TabsList>

                <TabsContent value="analyse" className="m-0">
                  <ScrollArea className="h-[55vh] p-4">
                    {selectedSession.analyse?.status === 'completed' ? (
                      <div className="space-y-6">
                        {/* Résumé */}
                        <div>
                          <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-500" />
                            Résumé
                          </h3>
                          <p className="text-sm bg-muted/50 p-3 rounded-lg">
                            {selectedSession.analyse.resume || "Pas de résumé disponible"}
                          </p>
                        </div>

                        {/* Points clés */}
                        {selectedSession.analyse.points_cles?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-lg mb-2">Points clés</h3>
                            <ul className="space-y-2">
                              {selectedSession.analyse.points_cles.map((point, i) => (
                                <li key={i} className="text-sm flex items-start gap-2 p-2 bg-muted/30 rounded">
                                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                                  {point}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Actions */}
                        {selectedSession.analyse.actions?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                              <ListTodo className="w-5 h-5 text-orange-500" />
                              Actions à suivre
                            </h3>
                            <div className="space-y-2">
                              {selectedSession.analyse.actions.map((action, i) => (
                                <div key={i} className="p-3 rounded-lg bg-muted/50 border">
                                  <p className="text-sm font-medium">{action.action}</p>
                                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                                    {action.responsable && (
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {action.responsable}
                                      </span>
                                    )}
                                    {action.priorite && (
                                      <Badge variant={
                                        action.priorite === 'haute' ? 'destructive' :
                                        action.priorite === 'moyenne' ? 'secondary' : 'outline'
                                      } className="text-xs">
                                        {action.priorite}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                        <Loader2 className="w-8 h-8 animate-spin mb-4" />
                        <p>Analyse en cours...</p>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="transcription" className="m-0">
                  <ScrollArea className="h-[55vh] p-4">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {selectedSession.transcription?.full_text || 'Transcription en cours...'}
                    </p>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="speakers" className="m-0">
                  <ScrollArea className="h-[55vh] p-4">
                    {selectedSession.transcription?.segments?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedSession.transcription.segments.map((segment, i) => (
                          <div key={i} className="p-3 rounded-lg bg-muted/30">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                <User className="w-3 h-3 mr-1" />
                                {segment.speaker}
                              </Badge>
                            </div>
                            <p className="text-sm">{segment.text}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground py-8">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Identification des speakers en cours...</p>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
